import styled from 'styled-components';
import { apiDelete, apiPatch, apiPost } from '../../../services/_common';
import { useEffect, useState } from 'react';
import AvatarIcon from '../../../components/AvatarIcon';
import { BtnBlue } from '../../../components/Button';
import { t } from 'i18next';
import { IApplicationFormType, IAttendance } from 'customTypes';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import Portal from '../../../components/Portal';
import DeleteModal from '../../../components/Modal/DeleteModal2';

type IReplyRowStyle = {
  isReplyComment: boolean;
  isEdit: boolean;
};

const ReplyStyle = styled.div`
  .total {
    padding: 1rem;
    color: var(--text-secondary);
  }

  .comment-wrapper {
    padding: 1rem;
    display: flex;
    gap: 0.5rem;

    button {
      height: 100%;
      max-height: 5rem;
    }

    @media screen and (max-width: 767px) {
      padding: 0.5rem;
    }
  }

  textarea {
    all: unset;
    width: inherit;
    padding: 0.5rem;
    color: var(--text-primary);
    background-color: var(--background);
    border: 1px solid var(--outline);
    border-radius: 6px;
    -webkit-transition: all 0.2s ease-in-out 0s;
    transition: all 0.2s ease-in-out 0s;
    overflow: auto;
    width: 100%;
  }

  @media print {
    display: none;
  }
`;

const ReplyRowStyle = styled.div<IReplyRowStyle>`
  display: flex;
  padding: ${({ isReplyComment }) => (isReplyComment ? '0' : '1rem')};
  border-top: ${({ isReplyComment }) => (!isReplyComment ? `1px solid var(--outline)` : 'none')};
  gap: 0.75rem;
  margin-left: ${({ isReplyComment }) => (isReplyComment ? '3rem' : '')};

  .circled-img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--tonal-light); // 배경색
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.2rem;
    color: var(--text-primary);

    img {
      width: inherit;
      height: inherit;
      object-fit: cover;
      border-radius: 40px;
    }
  }

  .reply {
    flex-grow: 1;

    .comment-wrapper {
      padding: 0;
    }

    > div {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.25rem;

      .flex-wrap {
        flex-wrap: wrap;
        justify-content: start;
      }

      div {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .badge {
          color: var(--tonal);
          background-color: var(--filled-slate-light);
          padding: 0 0.375rem;
          border-radius: 3px;
        }
      }
    }

    .new {
      color: var(--filled-amber);
      background-color: var(--tonal-amber);
      padding: 0 0.5rem;
      border-radius: 3px;
    }

    .bold {
      font-weight: var(--font-semibold);
    }

    .primary {
      color: var(--text-secondary);
    }

    .secondary {
      color: var(--text-disabled);
      font-size: 0.75rem;
    }

    .comment {
      > div {
        width: 100%;
      }
      > textarea {
        min-height: ${({ isEdit }) => (isEdit ? '3rem' : 'fit-content')};
        border: ${({ isEdit }) => (!isEdit ? 'none' : '')};
        background-color: ${({ isEdit }) => (!isEdit ? 'transparent' : '')};

        &:disabled {
          padding: 0.25rem 0;
        }
      }

      .secondary {
        padding-left: 0.5rem;
      }
    }

    .buttons {
      .small-btn {
        width: 2rem;
        min-width: fit-content;
        height: fit-content;
        cursor: pointer;
        text-align: center;
        display: flex;
        justify-content: center;
        border-radius: 3px;
        font-size: 0.875rem;
        padding: 0 0.25rem;
      }

      .red {
        color: var(--filled-red);
        background-color: var(--tonal-red);
        :hover {
          color: var(--tonal-red);
          background-color: var(--filled-red);
        }
      }
      .blue {
        color: var(--primary);
        background-color: var(--tonal-blue);
        :hover {
          color: var(--tonal-blue);
          background-color: var(--primary);
        }
      }
      .orange {
        color: var(--filled-amber);
        background-color: var(--tonal-amber);
        :hover {
          color: var(--tonal-amber);
          background-color: var(--filled-amber);
        }
      }
      .gray {
        color: var(--filled-slate);
        background-color: var(--tonal);
        :hover {
          // color: var(--tonal-deep);
          background-color: var(--tonal-deep);
        }
      }
    }
  }

  @media screen and (max-width: 767px) {
    margin-left: ${({ isReplyComment }) => (isReplyComment ? '1rem' : '')};
    gap: 0.5rem;
    padding: 0.5rem;
  }
`;

const ReplyRow = ({ rowState, data, formType }: { rowState: IAttendance; data: IReply[]; formType: IApplicationFormType }) => {
  const userInfo = useRecoilValue(userState);
  const [comments, setComments] = useState<IReply[]>(data);
  const [commentState, setCommentState] = useState('');
  const [commentReplyState, setCommentReplyState] = useState({ visible: false, isOriginalCmnt: true, seq: 0, oSeq: 0, aSeq: 0, contents: '', rUserId: '' });
  const [openModal, setOpenModal] = useState({ type: '', status: false });

  useEffect(() => {
    if (data) {
      setComments(data.map((el) => ({ ...el, disabled: true })));
      setCommentReplyState({ visible: false, isOriginalCmnt: true, seq: 0, oSeq: 0, aSeq: 0, contents: '', rUserId: '' });
    }
  }, [data]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setCommentState(value);
  };

  const handleNewCommentReplyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setCommentReplyState((prev) => ({ ...prev, contents: value }));
  };

  // 새 원본댓글 작성
  const handleClickNewComment = async () => {
    if (commentState?.trim().length <= 0) {
      toast.warning(t('내용을 입력하세요'));
      return;
    }
    try {
      const req = {
        cCd: rowState.cCd,
        caCd: rowState.caCd,
        rUserId: userInfo.userId,
        contents: commentState,
      };
      const res = await apiPost({ path: `/${formType}/comment`, contentType: 'application/json', req });
      if (res.data.statusCode === 200) {
        toast.success(t(res.data.message));
        setComments(res.data.data.map((el: IReply) => ({ ...el, disabled: true })));
        setCommentState('');
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const handleUpdateCommentReply = async (isDisable: boolean, cCdParam: string, caCdParam: string, seqParam: number, contentsParam: string) => {
    if (isDisable) {
      setCommentReplyState((prev) => ({ ...prev, visible: false })); // 답글 해제
      // 댓글 수정 불가능한 상태일 때 ( 수정 누른 댓글만 활성화 다른 댓글 수정은 비활성화 )
      const result = comments.map((el) => (el.seq === seqParam ? { ...el, disabled: false } : { ...el, disabled: true }));
      setComments(result);
    } else {
      // 수정가능 상태일 때
      try {
        const req = {
          cCd: cCdParam,
          caCd: caCdParam,
          seq: seqParam,
          contents: contentsParam,
        };
        const res = await apiPatch({ path: `/${formType}/comment`, contentType: 'application/json', req });
        if (res.data.statusCode === 200) {
          setComments(res.data.data.map((el: IReply) => ({ ...el, disabled: true })));
          setCommentState('');
          toast.success(t(res.data.message));
        }
      } catch (error) {
        console.error(error);
        throw new Error('error');
      }
    }
  };

  const handleCommentReplyChange = (e: React.ChangeEvent<HTMLTextAreaElement>, seqParam: number) => {
    const { value } = e.target;
    const result = comments.map((el) => (el.seq === seqParam ? { ...el, contents: value } : el));
    setComments(result);
  };

  const deleteAPI = async (cCdParam: string, caCdParam: string, seqParam: number) => {
    try {
      const req = { cCd: cCdParam, caCd: caCdParam, seq: seqParam };
      const res = await apiDelete({ path: `/${formType}/comment`, contentType: 'application/json', req });

      if (res.data.statusCode === 200) {
        setComments(res.data.data.map((el: IReply) => ({ ...el, disabled: true })));
        toast.success(t(res.data.message));
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const handleClickDelete = (cCdParam: string, caCdParam: string, seqParam: number) => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: () => deleteAPI(cCdParam, caCdParam, seqParam) }));
  };

  const handleClickCommentReply = (seqParam: number, oSeqParam: number, aSeqParam: number, rUserIdParam: string) => {
    const isOriginalCmnt = seqParam === oSeqParam && oSeqParam === aSeqParam && seqParam === aSeqParam;
    setComments(comments.map((el: IReply) => ({ ...el, disabled: true }))); // 댓글 수정 상태 해제
    setCommentReplyState({ visible: true, isOriginalCmnt, seq: seqParam, oSeq: oSeqParam, aSeq: isOriginalCmnt ? 0 : aSeqParam, contents: '', rUserId: rUserIdParam });
  };

  const handleClickNewCommentReply = async (cCd: string, caCd: string, oSeq: number, aSeq: number, rUserId: string) => {
    // 댓글인 경우 aSeq: 0,
    // 대댓글인 경우 aSeq: 부모댓글의 aSeq
    try {
      const req = { cCd, caCd, rUserId, oSeq, aSeq, contents: commentReplyState.contents };
      const res = await apiPost({ path: `/${formType}/comment`, contentType: 'application/json', req });
      if (res.data.statusCode === 200) {
        setComments(res.data.data.map((el: IReply) => ({ ...el, disabled: true })));
        setCommentReplyState({ visible: false, isOriginalCmnt: true, seq: 0, oSeq: 0, aSeq: 0, contents: '', rUserId: '' });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const renderIfWithin24Hours = (dateString: string): JSX.Element | null => {
    // 주어진 날짜 문자열을 Date 객체로 변환
    const givenDate = new Date(dateString);
    const currentDate = new Date();

    // 현재 시간과 주어진 날짜의 차이를 밀리초로 계산
    const timeDifference = currentDate.getTime() - givenDate.getTime();

    // 24시간(24 * 60 * 60 * 1000 밀리초) 이내인지 확인
    if (timeDifference <= 24 * 60 * 60 * 1000) {
      return <div className='new'>N</div>; // 24시간 이내인 경우 "N" 렌더링
    }

    return null; // 24시간이 지난 경우 아무것도 렌더링하지 않음
  };

  return (
    <ReplyStyle>
      <div className='total'>{comments.length > 0 ? `${t('댓글')} ${comments.length}${t('개')}` : t('댓글이 없습니다.')}</div>
      {comments.map(({ seq, oSeq, aSeq, cCd, caCd, contents, eDate, uImg, disabled, delYn, userId, rUserName, writer }) => (
        <ReplyRowStyle
          key={seq}
          isReplyComment={!(seq === oSeq && oSeq === aSeq && seq === aSeq)} // 댓글, 대댓글 여부에 따라 들여쓰기 처리. seq,oSeq,aSeq가 모두 동일한 경우 원댓글
          isEdit={!disabled}
        >
          <div className='circled-img'>{uImg ? <img src={uImg} alt='user_image' /> : <AvatarIcon />}</div>
          <div className='reply'>
            <div>
              <div className='flex-center flex-wrap'>
                {!(seq === oSeq && oSeq === aSeq && seq === aSeq) && <div className='badge'>{rUserName}</div>}
                <div className='bold primary'>{writer}</div>
                <div className='font-s secondary'>{eDate}</div>
                {renderIfWithin24Hours(eDate)}
              </div>
              {delYn !== 'Y' && (
                <div className='buttons'>
                  {/** 본인만 수정,삭제 */}
                  {userInfo.userId === userId && (
                    <>
                      {(!disabled || (commentReplyState.visible && commentReplyState.seq === seq)) && (
                        <div
                          className='small-btn gray cancel'
                          role='button'
                          tabIndex={0}
                          onClick={() => {
                            if (!disabled) {
                              const result = comments.map((el) => (el.seq === seq ? { ...el, disabled: true } : el));
                              setComments(result);
                            }

                            if (commentReplyState.visible && commentReplyState.seq === seq) {
                              setCommentReplyState((prev) => ({ ...prev, visible: false }));
                            }
                          }}
                        >
                          {t('취소')}
                        </div>
                      )}
                      <div className='small-btn orange ammend' role='button' tabIndex={0} onClick={() => handleUpdateCommentReply(disabled, cCd, caCd, seq, contents)}>
                        {disabled ? t('수정') : t('저장')}
                      </div>
                      <div className='small-btn red delete' role='button' tabIndex={0} onClick={() => handleClickDelete(cCd, caCd, seq)}>
                        {t('삭제')}
                      </div>
                    </>
                  )}
                  <div className='small-btn blue comment' role='button' tabIndex={0} onClick={() => handleClickCommentReply(seq, oSeq, aSeq, userId)}>
                    {t('답글')}
                  </div>
                </div>
              )}
            </div>
            <div className='comment'>
              {delYn === 'Y' ? <div className='secondary'>{t('삭제된 댓글입니다')}</div> : <textarea disabled={disabled} value={contents} onChange={(e) => handleCommentReplyChange(e, seq)} />}
            </div>

            {commentReplyState.visible && commentReplyState.seq === seq && (
              <div className='comment-wrapper'>
                <textarea onChange={(e) => handleNewCommentReplyChange(e)} />
                <div>
                  <BtnBlue onClick={() => handleClickNewCommentReply(cCd, caCd, oSeq, aSeq, userId)}>{t('등록')}</BtnBlue>
                </div>
              </div>
            )}
          </div>
        </ReplyRowStyle>
      ))}
      <div className='comment-wrapper'>
        <textarea value={commentState} onChange={(e) => handleCommentChange(e)} />
        <div>
          <BtnBlue onClick={handleClickNewComment}>{t('등록')}</BtnBlue>
        </div>
      </div>
      <Portal openModal={openModal?.status}>{openModal.status && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </ReplyStyle>
  );
};

export default ReplyRow;
